import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Calendar, Sparkles, Shield } from "lucide-react";

export default function Matches() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/matches").then(({ data }) => setMatches(data.matches)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <h1 className="font-display text-4xl md:text-5xl font-black text-charcoal flex items-center gap-2">
          <Heart className="w-8 h-8 text-terracotta" fill="#E07A5F" /> Your matches
        </h1>
        <p className="text-charcoal/60 mt-2">People you both liked. Say hi, then book a public hangout.</p>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {[...Array(3)].map((_, i) => <div key={i} className="h-44 bg-white rounded-2xl border border-border animate-pulse" />)}
          </div>
        ) : matches.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-border p-10 text-center mt-8">
            <Sparkles className="w-10 h-10 text-terracotta/40 mx-auto mb-3" />
            <p className="font-display text-xl font-bold text-charcoal">No matches yet</p>
            <p className="text-sm text-charcoal/60 mt-1 mb-4">Start swiping to find your people.</p>
            <Button data-testid="go-swipe" onClick={() => navigate("/swipe")} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">Start swiping</Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {matches.map((m) => (
              <div key={m.id} data-testid={`match-${m.id}`} className="bg-white rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300">
                <div className="flex gap-4 p-4">
                  <img src={m.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${m.name}`} alt={m.name} className="w-20 h-20 rounded-2xl object-cover" />
                  <div className="min-w-0">
                    <h3 className="font-display text-xl font-bold text-charcoal truncate">{m.name}{m.age ? `, ${m.age}` : ""}</h3>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-sage"><Shield className="w-3.5 h-3.5" /> {m.trust_score}%</span>
                    {m.personality_type && <p className="text-xs text-terracotta font-semibold mt-0.5">{m.personality_type}</p>}
                  </div>
                </div>
                <p className="px-4 text-sm text-charcoal/70 line-clamp-2">{m.bio}</p>
                <div className="flex gap-2 p-4">
                  <Button data-testid={`match-chat-${m.id}`} onClick={() => navigate(`/chat/${m.id}`)} className="flex-1 rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-9 text-sm"><MessageCircle className="w-4 h-4 mr-1" /> Chat</Button>
                  <Button data-testid={`match-book-${m.id}`} variant="outline" onClick={() => navigate(`/host/${m.id}`)} className="flex-1 rounded-full border-charcoal/20 h-9 text-sm"><Calendar className="w-4 h-4 mr-1" /> Book</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
